import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MediaOverlay } from './MediaOverlay';
import { youtubeEmbedUrl, youtubePosterUrl, youtubeThumbnailUrl } from './youtube';
import type { YoutubeVideoReference } from './youtube';

export interface YoutubeOverlayProps {
  video: YoutubeVideoReference | null;
  /** Dismisses the player back to the board, where the painted stand-in takes the surface over. */
  onClose?: () => void;
}

export function YoutubeOverlay({ video, onClose }: YoutubeOverlayProps) {
  const { t } = useTranslation(['board', 'common']);
  const [playing, setPlaying] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [posterFallback, setPosterFallback] = useState(false);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    setPlaying(false);
    setLoaded(false);
    setTimedOut(false);
    setPosterFallback(false);
    return () => {
      if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };
  }, [video]);

  useEffect(() => {
    if (!playing) return;
    timerRef.current = window.setTimeout(() => setTimedOut(true), 8000);
    return () => {
      if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };
  }, [playing]);

  if (!video) return null;

  // poster first, iframe only after the teacher taps play - no custom player chrome
  return (
    <MediaOverlay title={t('media.youtubePlayer')} bare={true} onClose={onClose}>
      {playing ? (
        <div className="youtube-overlay__frame">
          {!loaded && !timedOut ? <div className="youtube-overlay__loading">{t('media.loading')}</div> : null}
          {timedOut && !loaded ? <div className="youtube-overlay__error"><strong>{t('media.offline')}</strong>{onClose ? <button type="button" className="secondary-button" onClick={onClose}>{t('common:actions.close')}</button> : null}</div> : null}
          <iframe src={youtubeEmbedUrl(video)} title={t('media.youtubeFrame')} frameBorder="0" allow="autoplay; encrypted-media; fullscreen" allowFullScreen={true} onLoad={() => { if (timerRef.current !== undefined) window.clearTimeout(timerRef.current); setLoaded(true); setTimedOut(false); }} />
        </div>
      ) : (
        <button type="button" className="yt-poster" aria-label={t('media.play')} onClick={() => setPlaying(true)}>
          {/* a missing maxres 404s with a 120x90 grey body that Chrome loads, so size is the test, not onError */}
          <img src={posterFallback ? youtubeThumbnailUrl(video.id) : youtubePosterUrl(video.id)} alt=""
            onLoad={(event) => { if (event.currentTarget.naturalWidth <= 120) setPosterFallback(true); }}
            onError={() => setPosterFallback(true)} />
          <span className="yt-poster__play" aria-hidden={true}>
            <svg width="62" height="62" viewBox="0 0 16 16" fill="currentColor" aria-hidden={true}><path d="M5.4 3.2 12.4 8l-7 4.8z" /></svg>
          </span>
        </button>
      )}
    </MediaOverlay>
  );
}
